/**
 * navConfig — single source of truth for account + admin navigation.
 *
 * Phase B (2026-06-11): extracted from V2Nav so BOTH the desktop sidebar and the
 * mobile More sheet consume the same definitions and can never drift again. The
 * admin items are additionally GROUPED by purpose here so the mobile Admin Tools
 * sub-sheet (and, later, the desktop sidebar) can render meaningful sections
 * instead of one flat 14-item list.
 *
 * Keep this file presentation-light: it exports data + small inline SVG icons.
 */
import type { ReactNode } from 'react';

export interface NavItem {
  label: string;
  href: string;
  icon: ReactNode;
  /** Optional one-line description shown on roomy surfaces (mobile sheet). */
  desc?: string;
}

export interface NavGroup {
  /** Section header, e.g. "Users & Growth". */
  title: string;
  items: NavItem[];
}

// ── Account (every signed-in user) ──────────────────────────────────────────
export const ACCOUNT_ITEMS: NavItem[] = [
  { label: 'Usage', href: '/dashboard/usage', desc: 'Streaming minutes & quota', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>
  ) },
  { label: 'Billing', href: '/dashboard/billing', desc: 'Plan & payment', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
  ) },
  { label: 'Settings', href: '/dashboard/settings', desc: 'Preferences & account', icon: (
    <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M6.5 1h3l.5 1.8a5 5 0 0 1 1.2.7l1.8-.6 1.5 2.6-1.4 1.2a5 5 0 0 1 0 1.6l1.4 1.2-1.5 2.6-1.8-.6a5 5 0 0 1-1.2.7L9.5 15h-3l-.5-1.8a5 5 0 0 1-1.2-.7l-1.8.6-1.5-2.6 1.4-1.2a5 5 0 0 1 0-1.6L1.5 6.4 3 3.8l1.8.6a5 5 0 0 1 1.2-.7L6.5 1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3"/></svg>
  ) },
];

// ── Admin, grouped by purpose ───────────────────────────────────────────────
// Same routes as the desktop SETTINGS_ITEMS_ADMIN list (kept in sync), now
// organized into sections for a scannable mobile Admin Tools sheet.
export const ADMIN_GROUPS: NavGroup[] = [
  {
    title: 'Users & Growth',
    items: [
      { label: 'Waitlist', href: '/dashboard/admin', desc: 'Invites · acceptance', icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
      ) },
      { label: 'Email Queue', href: '/dashboard/admin/emails', icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
      ) },
      { label: 'Customer CRM', href: '/dashboard/leads', icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
      ) },
      { label: 'Design Partners', href: '/dashboard/admin/design-partners', desc: 'Pilot agreements · click-to-sign', icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
      ) },
      { label: 'Growth', href: '/dashboard/growth', icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>
      ) },
      { label: 'Win/Loss', href: '/dashboard/admin/winloss', icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M8 21h8M12 17v4M17 5h2a2 2 0 010 4h-2"/><path d="M7 5H5a2 2 0 000 4h2"/><path d="M7 5a5 5 0 0010 0V3H7v2z"/></svg>
      ) },
    ],
  },
  {
    title: 'Fundraise',
    items: [
      { label: 'Fundraise', href: '/dashboard/investors', icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
      ) },
      { label: 'Investor CRM', href: '/dashboard/investor-crm', icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
      ) },
    ],
  },
  {
    title: 'Platform',
    items: [
      { label: 'API Quotas', href: '/dashboard/admin/pricing', icon: (
        <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M6.5 1h3l.5 1.8a5 5 0 0 1 1.2.7l1.8-.6 1.5 2.6-1.4 1.2a5 5 0 0 1 0 1.6l1.4 1.2-1.5 2.6-1.8-.6a5 5 0 0 1-1.2.7L9.5 15h-3l-.5-1.8a5 5 0 0 1-1.2-.7l-1.8.6-1.5-2.6 1.4-1.2a5 5 0 0 1 0-1.6L1.5 6.4 3 3.8l1.8.6a5 5 0 0 1 1.2-.7L6.5 1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3"/></svg>
      ) },
      { label: 'API Settings', href: '/dashboard/admin/api-settings', icon: (
        <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><rect x="1" y="3" width="14" height="2" rx="1" fill="currentColor" opacity="0.6"/><rect x="1" y="7" width="9" height="2" rx="1" fill="currentColor" opacity="0.6"/><rect x="1" y="11" width="11" height="2" rx="1" fill="currentColor" opacity="0.6"/></svg>
      ) },
      { label: 'DevOps', href: '/dashboard/admin/devops', icon: (
        <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4"/><path d="M8 5v3l2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
      ) },
      { label: 'Demo Library', href: '/dashboard/admin/demo-library', icon: (
        <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><rect x="1" y="2" width="14" height="2" rx="1" fill="currentColor" opacity="0.6"/><rect x="1" y="7" width="14" height="2" rx="1" fill="currentColor" opacity="0.6"/><rect x="1" y="12" width="9" height="2" rx="1" fill="currentColor" opacity="0.6"/></svg>
      ) },
      { label: 'Prompts', href: '/dashboard/admin/prompts', icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
      ) },
    ],
  },
  {
    title: 'Content',
    items: [
      { label: 'Blog Admin', href: '/dashboard/blog', icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
      ) },
      { label: 'Careers Admin', href: '/dashboard/careers', icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
      ) },
    ],
  },
];

/** Flat list of every admin item (derived) — useful for counts and desktop parity. */
export const ADMIN_ITEMS_FLAT: NavItem[] = ADMIN_GROUPS.flatMap(g => g.items);

/** Total admin destination count — drives the "Admin Tools · N" label. */
export const ADMIN_ITEM_COUNT = ADMIN_ITEMS_FLAT.length;
